import React, { Component, ReactNode } from 'react';
import { StyledActionButtons } from './ActionButtonsStyle';
import { SelectionAddIcon, RulerIcon } from '@inter-ikea-kompis/icons';
import { SofaIcons } from '../../typings/enums';
import { Orientation, DeviceType, WriteDirection, Platform } from 'sofa-reborn-react';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import RotateLeftIconPaths from '@ingka/ssr-icon/paths/rotate-left';
import { PopoverDirectionEnum } from '@inter-ikea-kompis/component-popover';

import {
	KompisIconPill,
	KompisIconPillGroup,
	KompisTooltip,
} from '@inter-ikea-kompis/react-components';
import SkapaIconButton from '@ingka/button';

import { localeService } from '../../services';

type Props = {
	orientation: Orientation;
	deviceType: DeviceType;
	platform: Platform;
	writeDirection: WriteDirection;
	showFunctionSleep?: boolean;
	showFunctionStorage?: boolean;
	showFunctions?: boolean;
	showFunctionsButtonSleep?: boolean;
	showFunctionsButtonStorage?: boolean;
	showMeasurements?: boolean;
	showSelectAll?: boolean;
	showSelectAllButton?: boolean;
	disableShowMeasurements?: boolean;
	disableShowFunctionSleep?: boolean;
	disableShowFunctionStorage?: boolean;
	disableShowFunctions?: boolean;
	disableShowSelectAll?: boolean;
	disableRotateButton?: boolean;
	onFunctionSleepClick?: () => void;
	onFunctionStorageClick?: () => void;
	onMeasurementsClick?: () => void;
	onRotationClick?: () => void;
	onSelectAllClick?: () => void;
};

/**
 * React component.
 *
 */
export default class ActionButtons extends Component<Props> {
	/**
	 * React render method.
	 *
	 * @returns React node.
	 */
	public render(): ReactNode {
		const { props } = this;
		const {
			onFunctionSleepClick,
			onFunctionStorageClick,
			onMeasurementsClick,
			onRotationClick,
			onSelectAllClick,
			deviceType,
			platform,
		} = props;

		const renderTooltip =
			deviceType !== 'mobile' && deviceType !== 'tablet' && platform !== 'kiosk';

		const IconPillMeasurement = (
			<KompisIconPill
				theme={SkapaTheme}
				icon={RulerIcon}
				selected={props.showMeasurements as boolean}
				disabled={props.disableShowMeasurements as boolean}
				onSelect={onMeasurementsClick}
				ariaLabel={localeService.getTranslation('tooltipShowSize')}
				// @ts-expect-error Missmatched kompis type
				onKeyDown={(e): void => {
					if (e.key === 'Enter' || e.key === ' ') {
						onMeasurementsClick && onMeasurementsClick();
					}
				}}
			></KompisIconPill>
		);
		const IconPillSelectAll = (
			<KompisIconPill
				theme={SkapaTheme}
				icon={SelectionAddIcon}
				selected={props.showSelectAll as boolean}
				disabled={props.disableShowSelectAll as boolean}
				onSelect={onSelectAllClick}
				ariaLabel={localeService.getTranslation('tooltipSelectAll')}
				// @ts-expect-error Missmatched kompis type
				onKeyDown={(e): void => {
					if (e.key === 'Enter' || e.key === ' ') {
						onSelectAllClick && onSelectAllClick();
					}
				}}
			></KompisIconPill>
		);
		const IconPillFunctionSleep = (
			<KompisIconPill
				theme={SkapaTheme}
				icon={SofaIcons.FunctionSleepIcon}
				selected={props.showFunctionSleep as boolean}
				disabled={props.disableShowFunctionSleep as boolean}
				onSelect={onFunctionSleepClick}
				ariaLabel={
					props.disableShowFunctionSleep
						? localeService.getTranslation('disabledFunctionButtonSleepTooltip')
						: localeService.getTranslation('functionButtonSleepTooltip')
				}
				// @ts-expect-error Missmatched kompis type
				onKeyDown={(e): void => {
					if (e.key === 'Enter' || e.key === ' ') {
						onFunctionSleepClick && onFunctionSleepClick();
					}
				}}
			></KompisIconPill>
		);
		const IconPillFunctionStorage = (
			<KompisIconPill
				theme={SkapaTheme}
				icon={SofaIcons.FunctionStorageIcon}
				selected={props.showFunctionStorage as boolean}
				disabled={props.disableShowFunctionStorage as boolean}
				onSelect={onFunctionStorageClick}
				ariaLabel={
					props.disableShowFunctionStorage
						? localeService.getTranslation('disabledFunctionButtonStorageTooltip')
						: localeService.getTranslation('functionButtonStorageTooltip')
				}
				// @ts-expect-error Missmatched kompis type
				onKeyDown={(e): void => {
					if (e.key === 'Enter' || e.key === ' ') {
						onFunctionStorageClick && onFunctionStorageClick();
					}
				}}
			></KompisIconPill>
		);
		const IconButtonRotate = (
			<SkapaIconButton
				id="rotateButton"
				iconOnly
				ssrIcon={RotateLeftIconPaths}
				type={'secondary'}
				onClick={onRotationClick}
				aria-label={localeService.getTranslation('tooltipRotateAll')}
				disabled={props.disableRotateButton}
			/>
		);
		return (
			<StyledActionButtons
				deviceType={props.deviceType}
				orientation={props.orientation}
				theme={SkapaTheme}
			>
				{renderTooltip ? (
					<KompisIconPillGroup theme={SkapaTheme}>
						<KompisTooltip
							theme={SkapaTheme}
							text={localeService.getTranslation('tooltipShowSize')}
							direction={PopoverDirectionEnum.top}
						>
							{IconPillMeasurement}
						</KompisTooltip>
						{props.showSelectAllButton && (
							<KompisTooltip
								theme={SkapaTheme}
								text={localeService.getTranslation('tooltipSelectAll')}
								direction={PopoverDirectionEnum.top}
							>
								{IconPillSelectAll}
							</KompisTooltip>
						)}
						{props.showFunctionsButtonSleep && (
							<KompisTooltip
								theme={SkapaTheme}
								text={localeService.getTranslation('functionButtonSleepTooltip')}
								direction={PopoverDirectionEnum.top}
							>
								{IconPillFunctionSleep}
							</KompisTooltip>
						)}
						{props.showFunctionsButtonStorage && (
							<KompisTooltip
								theme={SkapaTheme}
								text={localeService.getTranslation('functionButtonStorageTooltip')}
								direction={PopoverDirectionEnum.top}
							>
								{IconPillFunctionStorage}
							</KompisTooltip>
						)}
					</KompisIconPillGroup>
				) : (
					<KompisIconPillGroup theme={SkapaTheme}>
						{IconPillMeasurement}
						{props.showSelectAllButton && IconPillSelectAll}
						{props.showFunctionsButtonSleep && IconPillFunctionSleep}
						{props.showFunctionsButtonStorage && IconPillFunctionStorage}
					</KompisIconPillGroup>
				)}
				{renderTooltip && !props.disableRotateButton ? (
					<KompisTooltip
						theme={SkapaTheme}
						text={localeService.getTranslation('tooltipRotateAll')}
						direction={PopoverDirectionEnum.top}
					>
						{IconButtonRotate}
					</KompisTooltip>
				) : (
					IconButtonRotate
				)}
			</StyledActionButtons>
		);
	}
}
